import type { PropsWithChildren } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Container } from '@mui/material';
import { SidebarProvider } from './SidebarContext';
import { Topbar } from './Topbar';
import type { TopbarProps } from './Topbar';
import { Sidebar } from './Sidebar';
import type { SidebarProps } from './Sidebar';
import { LAYOUT_BORDER_OFFSET, SIDE_NAV_WIDTH } from './constants';

const LayoutRoot = styled('div')(({ theme }) => ({
    display: 'flex',
    flex: '1 1 auto',
    maxWidth: '100%',
    paddingLeft: 0,
    [theme.breakpoints.up('lg')]: {
        paddingLeft: SIDE_NAV_WIDTH,
    },
}));

const borderOffset = LAYOUT_BORDER_OFFSET;
const LayoutContainer = styled(Box)({
    paddingTop: borderOffset,
    paddingBottom: borderOffset,
    flex: '1 1 auto',
    flexDirection: 'column',
    width: '100%',
    position: 'relative',
    minHeight: `calc(100vh - ${borderOffset * 2}px)`,
    display: 'flex',
});

export type SidebarLayoutProps = PropsWithChildren<SidebarProps & TopbarProps>;

function SidebarLayoutInner({
    children,
    entries,
    logo,
    appName,
    pageDescription,
    explanationVideo,
    resetPageData,
    userInitial,
    renderAccountPopover,
    rightSlot,
}: SidebarLayoutProps) {
    return (
        <>
            <Topbar
                pageDescription={pageDescription}
                explanationVideo={explanationVideo}
                resetPageData={resetPageData}
                userInitial={userInitial}
                renderAccountPopover={renderAccountPopover}
                rightSlot={rightSlot}
            />
            <Sidebar entries={entries} logo={logo} appName={appName} />
            <LayoutRoot>
                <LayoutContainer sx={{ mt: `${borderOffset}px` }}>
                    <Container
                        maxWidth={false}
                        disableGutters
                        sx={{ px: { xs: 2, sm: 4, lg: 8 } }}
                    >
                        {children}
                    </Container>
                </LayoutContainer>
            </LayoutRoot>
        </>
    );
}

export function SidebarLayout(props: SidebarLayoutProps) {
    return (
        <SidebarProvider>
            <SidebarLayoutInner {...props} />
        </SidebarProvider>
    );
}
